import React, { useMemo } from 'react'
import { convertPointsToPrice } from 'constants/merchandise'
import qs from 'qs'
import { useAppSelector } from 'hooks/reduxHooks'
import FormatLoyaltyPoints from 'loyalty/components/Formatters/FormatLoyaltyPoints'
import { selectLoggedIn } from 'selectors/accountSelectors'
import MerchandiseOfferCarousel from './MerchandiseOfferCarousel'

const MAX_POINTS = 25000000
const MIN_POINTS = 2500
const PREMIUM_FLOOR_POINTS = 375000
const PREMIUM_THRESHOLD = 1000000

const BUFFER_TIERS = [
  { minPoints: 159300, buffer: 0.10 },
  { minPoints: 141600, buffer: 0.20 },
  { minPoints: 129000, buffer: 0.30 },
  { minPoints: 106200, buffer: 0.40 },
] as const

const DEFAULT_BUFFER = 0.50

function isPremiumUser(points: number): boolean {
  return points >= PREMIUM_THRESHOLD
}

function getBufferPercentage(points: number): number {
  const tier = BUFFER_TIERS.find(t => points >= t.minPoints)
  return tier?.buffer ?? DEFAULT_BUFFER
}

function calculatePriceRange(points: number): { min: number; max: number } {
  if (isPremiumUser(points)) {
    return {
      min: convertPointsToPrice(PREMIUM_FLOOR_POINTS),
      max: convertPointsToPrice(MAX_POINTS),
    }
  }

  const buffer = getBufferPercentage(points)
  const calculatedMin = Math.floor(points * (1 - buffer))
  const calculatedMax = Math.floor(points * (1 + buffer))

  const finalMax = Math.min(MAX_POINTS, calculatedMax)
  const finalMin = Math.max(MIN_POINTS, Math.min(calculatedMin, finalMax))

  return {
    min: convertPointsToPrice(finalMin),
    max: convertPointsToPrice(finalMax),
  }
}

interface Props {
  filters?: App.MerchandiseFilters
  trackingPageName: string
}

function MerchandisePointsCarousel({ filters, trackingPageName }: Props) {
  const points = useAppSelector(state => state.loyalty.account.data?.points ?? 0)
  const isLoggedIn = useAppSelector(state => selectLoggedIn(state))

  const shouldShowCarousel = isLoggedIn && points >= MIN_POINTS

  const pointsFilter = useMemo((): App.MerchandiseFilters | undefined => {
    if (!shouldShowCarousel) return

    const priceRange = calculatePriceRange(points)

    return {
      ...(filters ?? {}),
      price: priceRange,
      sortBy: 'priceAsc',
    }
  }, [points, shouldShowCarousel, filters])

  const pointsUrl = useMemo(() => {
    if (!shouldShowCarousel || !pointsFilter) return

    return `/search/shop?${qs.stringify({
      ...(filters ?? {}),
      'price.min': pointsFilter?.price?.min,
      'price.max': pointsFilter?.price?.max,
      sortBy: 'priceAsc',
    })}`
  }, [pointsFilter, shouldShowCarousel, filters])

  if (!shouldShowCarousel) {
    return null
  }

  return (
    <MerchandiseOfferCarousel
      title={<>Shop with your <em><FormatLoyaltyPoints points={points} abbreviated /></em></>}
      filters={pointsFilter}
      viewAllTo={pointsUrl}
      trackingListName="Shop with your points"
      trackingPageName={trackingPageName}
    />
  )
}

export default MerchandisePointsCarousel
